// Admin maintenance toggle. Compliance-friendly layout: lots of
// whitespace, primary action stands alone, side-effects spelled out.

import { useState } from 'react';
import { Wrench, Lock, AlertTriangle } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { useMaintenance, setMaintenance } from '../../api/maintenance';

function maintenanceStatusBadge(isLoading: boolean, active: boolean) {
  if (isLoading) return <Badge tone="neutral">Loading…</Badge>;
  if (active) {
    return (
      <Badge tone="warning">
        <Lock className="h-3 w-3" />
        Active — read-only
      </Badge>
    );
  }
  return <Badge tone="success">Available</Badge>;
}

export function AdminMaintenance() {
  const { data, mutate, isLoading } = useMaintenance();
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const enable = async () => {
    setBusy(true);
    setErr(null);
    try {
      await setMaintenance({
        active: true,
        reason: reason.trim(),
        until: until ? new Date(until).toISOString() : null,
      });
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setErr(null);
    try {
      await setMaintenance({ active: false });
      setReason('');
      setUntil('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const active = data?.active ?? false;

  return (
    <AdminShell
      title="Maintenance mode"
      description="Put the platform into read-only mode for planned migrations, schema changes, or upstream cutovers. While active, every write request returns 503 Service Unavailable; reads continue to work."
    >
      <Card>
        <CardHeader
          title="Current state"
          description="Polled by all running clients every 30 seconds; toggle takes effect within seconds."
          actions={maintenanceStatusBadge(isLoading, active)}
        />
        <CardBody>
          {active ? (
            <div className="space-y-3">
              {data?.reason && (
                <div>
                  <div className="orb-section-title">Reason</div>
                  <p className="text-fg mt-1">{data.reason}</p>
                </div>
              )}
              {data?.until && (
                <div>
                  <div className="orb-section-title">Scheduled end</div>
                  <p className="text-fg mt-1">{new Date(data.until).toLocaleString()}</p>
                </div>
              )}
              {data?.set_by && (
                <div>
                  <div className="orb-section-title">Set by</div>
                  <p className="text-fg mt-1">{data.set_by}</p>
                </div>
              )}
              <div>
                <div className="orb-section-title">Last updated</div>
                <p className="text-fg mt-1">
                  {data?.updated_at && new Date(data.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-fg-muted">Platform is operating normally. Read and write requests are processed.</p>
          )}
        </CardBody>
        {active && (
          <CardFooter className="flex justify-end">
            <Button variant="primary" disabled={busy} onClick={disable}>
              <Wrench className="h-4 w-4" />
              Disable maintenance mode
            </Button>
          </CardFooter>
        )}
      </Card>

      {!active && (
        <Card>
          <CardHeader
            title="Enable maintenance mode"
            description="Optional context — a reason and a scheduled end time — is shown to every client during the maintenance window."
          />
          <CardBody className="space-y-4">
            <div>
              <label htmlFor="maintenance-reason" className="block text-sm font-medium text-fg mb-1.5">
                Reason
              </label>
              <Input
                id="maintenance-reason"
                placeholder="e.g. Postgres major-version upgrade"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <p className="text-xs text-fg-subtle mt-1">
                Plain text. Shown verbatim in the banner across all clients.
              </p>
            </div>
            <div>
              <label htmlFor="maintenance-until" className="block text-sm font-medium text-fg mb-1.5">
                Scheduled end (optional)
              </label>
              <Input
                id="maintenance-until"
                type="datetime-local"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
              />
              <p className="text-xs text-fg-subtle mt-1">
                Leave empty if you'll disable the flag manually when work is done.
              </p>
            </div>

            <div className="rounded-md bg-warning/5 border border-warning/30 p-3 flex items-start gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <div className="text-fg-muted">
                <span className="font-medium text-fg">All write endpoints will return 503</span> while
                maintenance is active — including artifact uploads, repo
                creates, retention rule edits, and Build-Info submissions.
                Read endpoints stay available.
              </div>
            </div>
          </CardBody>
          <CardFooter className="flex justify-end gap-2">
            <Button variant="secondary" disabled={busy} onClick={() => { setReason(''); setUntil(''); }}>
              Clear
            </Button>
            <Button variant="primary" disabled={busy} onClick={enable}>
              <Wrench className="h-4 w-4" />
              Enable maintenance mode
            </Button>
          </CardFooter>
        </Card>
      )}

      {err && (
        <Card className="border-danger/30 bg-danger/5">
          <CardBody>
            <p className="text-danger font-mono text-sm">{err}</p>
          </CardBody>
        </Card>
      )}
    </AdminShell>
  );
}
